import React from 'react';
import {
    Modal,
    ModalBody,
    ModalFooter,
    FormGroup,
    Input as ReactstrapInput,
    Label,
    UncontrolledTooltip,
} from 'reactstrap';
import cls from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleNotch, faTimes, faSearch, faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import ValidatedSelect, { CompactSelectStyles } from '../common/forms/ValidatedSelect';
import { FlexBetweenRow, IconButton, Input } from '../common/forms/FormElements';
import { SaveButton } from '../common/forms/SaveButton';
import StaffCard from './StaffCard';
import EquipmentCard from './EquipmentCard';
import { DraggableResourceTypes, getJobAssignmentCancellationTypeIdsThatPreventReassignment } from './Dispatch';
import DispatchEmployeeFilter from './DispatchEmployeeFilter';
import './MobileAssignmentsModal.scss';

class MobileAssignmentsModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            searchText: '',
        };
        this.searchInputRef = React.createRef();
        this.modalRef = React.createRef();
    }

    handleSearchChange = (e) => {
        this.setState({ searchText: e.target.value });
    };

    handleClick = (resource) => {
        const { selectedResource, onResourceSelect } = this.props;

        // Toggle selection - if already selected, deselect it
        if (selectedResource?.id === resource.id) {
            onResourceSelect(null);
        } else {
            onResourceSelect(resource);
        }
    };

    getFilteredResources = () => {
        const { searchText } = this.state;
        const {
            resourceType, resources, hiddenEquipmentIds, hiddenStaffIds,
        } = this.props;

        // Get the appropriate resource list and filter out invalid entries
        let resourceList = resourceType === DraggableResourceTypes.Staff
            ? (resources?.staff?.rows || [])
                .filter((resource) => resource && resource.id && resource.name)
                .filter((resource) => !hiddenStaffIds?.includes(resource.id)) // Remove hidden staff
            : (resources?.equipment?.rows || [])
                .filter((resource) => resource && resource.id && (resource.description || resource.name))
                .filter((resource) => !hiddenEquipmentIds?.includes(resource.id)); // Remove hidden equipment

        // Apply search filter
        if (searchText) {
            const searchLower = searchText.toLowerCase();
            resourceList = resourceList.filter((resource) => {
                if (resourceType === DraggableResourceTypes.Staff) {
                    return resource.name?.toLowerCase().includes(searchLower);
                } else {
                    return (resource.description || resource.name || '')
                        .toLowerCase()
                        .includes(searchLower);
                }
            });
        }

        return resourceList;
    };

    render() {
        const {
            isOpen,
            onClose,
            resourceType,
            resources,
            selectedResource,
            onSave,
            tenantSettings,
            hiddenStaffIds,
            hiddenEquipmentIds,
            longTermStatuses,
            employeeLongTermStatuses,
            jobAssignmentCancellationTypes,
            isSaving = false,
            // Dispatch office switcher props
            allowCrossDispatching,
            dispatchEquipmentPermission,
            dispatchEmployeePermission,
            dispatchLocations,
            resourceLocation,
            onSelectedResourceLocationChanged,
            // Card display props
            resourceJobs,
            daysInRange,
            dailyAttendanceEvents,
            employeeDailyAttendanceEvents,
            cancellationJobs,
            selectedJobDispatchLocation,
            resourcesLoading,
            // Filter props
            selectedWeekRangeDays,
            areAttendanceEventsEnabled,
            allJobs,
            staffLoading,
            // Filter props from DispatchBoard
            onlyShowUnassigned,
            onlyShowAttendance,
            onlyShowCancelled,
            onUnassignedClick,
            onAttendanceClick,
            onCancelledClick,
            // Bulk assignment props
            bulkAssignmentEnabled,
            onBulkAssignmentToggle,
        } = this.props;

        const { searchText } = this.state;

        if (!isOpen) return null;

        const resourceList = this.getFilteredResources();

        // Check for cross-dispatching (same logic as desktop)
        const isCrossDispatching = selectedJobDispatchLocation != resourceLocation?.value;

        // Calculate cancellation type IDs that prevent reassignment
        const jobAssignmentCancellationTypeIdsThatPreventReassignment = getJobAssignmentCancellationTypeIdsThatPreventReassignment(jobAssignmentCancellationTypes);

        return (
            <Modal
                isOpen={isOpen}
                toggle={onClose}
                className="modal-mobile-fullscreen resource-modal"
                contentClassName="h-100"
                backdrop={false}
                fade={false}
            >
                <div className="modal-header d-flex flex-column h-auto flex-none">
                    <FlexBetweenRow className="w-100 mb-3">
                        {(allowCrossDispatching && (dispatchEquipmentPermission || dispatchEmployeePermission) && dispatchLocations && dispatchLocations.length > 1) ? (
                            <ValidatedSelect
                                placeholder="Select Office"
                                styles={CompactSelectStyles}
                                options={dispatchLocations}
                                value={resourceLocation}
                                onChange={onSelectedResourceLocationChanged}
                                isClearable={false}
                                formGroupClassName="resource-dropdown"
                                size="lg"
                            />
                        ) : <div />}

                        <IconButton
                            size="lg"
                            onClick={onClose}
                            aria-label="Close"
                            className="ml-3"
                        >
                            <FontAwesomeIcon
                                icon={faTimes}
                                className="text-muted"
                                size="lg"
                            />
                        </IconButton>
                    </FlexBetweenRow>

                    <Input
                        placeholder={`Search ${resourceType === DraggableResourceTypes.Staff ? 'Staff' : 'Equipment'}`}
                        type="search"
                        value={searchText}
                        onChange={this.handleSearchChange}
                        formGroupClassName="w-100 mb-0"
                        startAddonClassName="addon-centered"
                        startAddon={(
                            <FontAwesomeIcon
                                icon={faSearch}
                                className="text-muted"
                            />
                        )}
                    />

                    {resourceType === DraggableResourceTypes.Staff && tenantSettings?.dispatchStatusFilterEnabled && (
                        <DispatchEmployeeFilter
                            dailyAttendanceEvents={dailyAttendanceEvents}
                            employeeDailyAttendanceEvents={employeeDailyAttendanceEvents}
                            selectedWeekRangeDays={selectedWeekRangeDays}
                            loading={false}
                            staffLoading={staffLoading}
                            areAttendanceEventsEnabled={areAttendanceEventsEnabled}
                            staff={resources?.staff}
                            allJobs={allJobs}
                            employeeLongTermStatuses={employeeLongTermStatuses}
                            onlyShowAttendance={onlyShowAttendance}
                            onlyShowUnassigned={onlyShowUnassigned}
                            onlyShowCancelled={onlyShowCancelled}
                            onAttendanceClick={onAttendanceClick}
                            onUnassignedClick={onUnassignedClick}
                            onCancelledClick={onCancelledClick}
                            staffTabActive
                            tenantSettings={tenantSettings}
                            resourcesLoading={resourcesLoading}
                            cancellationJobs={cancellationJobs}
                            jobAssignmentCancellationTypes={jobAssignmentCancellationTypes}
                            mobile
                        />
                    )}
                </div>

                <ModalBody className={cls({ highlight: isCrossDispatching })}>
                    {resourcesLoading ? (
                        <div className="loading-container">
                            <FontAwesomeIcon
                                icon={faCircleNotch}
                                className="fa-spin loading-spinner"
                                size="2x"
                            />
                            <p className="loading-text">Loading resources...</p>
                        </div>
                    ) : (
                        <dl
                            id="dispatchCenterContentArea"
                        >
                            {resourceList.map((resource) => {
                                if (resourceType === DraggableResourceTypes.Staff) {
                                    return (
                                        <StaffCard
                                            key={resource.id}
                                            staff={resource}
                                            compact
                                            tenantSettings={tenantSettings}
                                            hiddenStaffIds={hiddenStaffIds || []}
                                            longTermStatuses={longTermStatuses || []}
                                            employeeLongTermStatuses={employeeLongTermStatuses || []}
                                            jobAssignmentCancellationTypes={jobAssignmentCancellationTypes || []}
                                            jobAssignmentCancellationTypeIdsThatPreventReassignment={jobAssignmentCancellationTypeIdsThatPreventReassignment}
                                            jobs={resourceJobs || []}
                                            weekdays={daysInRange || []}
                                            cancellationJobs={cancellationJobs || []}
                                            dailyAttendanceEvents={dailyAttendanceEvents || []}
                                            employeeDailyAttendanceEvents={employeeDailyAttendanceEvents || []}
                                            selected={selectedResource?.id === resource.id}
                                            dragging={false}
                                            minimal={false}
                                            onClick={() => this.handleClick(resource)}
                                            provided={{
                                                draggableProps: {},
                                                dragHandleProps: {},
                                                innerRef: () => { },
                                            }}
                                            innerRef={() => { }}
                                        />
                                    );
                                }

                                return (
                                    <EquipmentCard
                                        key={resource.id}
                                        equipment={resource}
                                        compact
                                        tenantSettings={tenantSettings}
                                        allJobs={resourceJobs || []}
                                        weekdays={daysInRange || []}
                                        hiddenEquipmentIds={hiddenEquipmentIds || []}
                                        onClick={() => this.handleClick(resource)}
                                        selected={selectedResource?.id === resource.id}
                                        dragging={false}
                                        minimal={false}
                                        provided={{
                                            draggableProps: {},
                                            dragHandleProps: {},
                                            innerRef: () => { },
                                        }}
                                        innerRef={() => { }}
                                    />
                                );
                            })}
                            {resourceList.length === 0 && (
                                <div className="no-resources">
                                    <p>No {resourceType === DraggableResourceTypes.Staff ? 'staff members' : 'equipment'} available</p>
                                </div>
                            )}
                        </dl>
                    )}
                </ModalBody>

                <ModalFooter className="flex-none">
                    <div className="d-flex flex-column w-100">
                        {resourceType === DraggableResourceTypes.Equipment &&
                            (this.props.slot?.eligibleStaff || this.props.slot?.eligibleEquipment) && (
                                <div className="mb-3">
                                    <label className="form-label">
                                        {this.props.slot.eligibleEquipment?.length > 0 ? 'Assign To Equipment' : 'Assign To Staff'}
                                        {(this.props.slot.eligibleStaff?.length === 0 && this.props.slot.eligibleEquipment?.length === 0) && (
                                            <span className="text-muted ml-2">(No eligible resources)</span>
                                        )}
                                    </label>
                                    <ValidatedSelect
                                        placeholder={this.props.slot.eligibleEquipment?.length > 0 ? 'Select Equipment' : 'Select Staff Member'}
                                        styles={CompactSelectStyles}
                                        options={this.props.slot.eligibleEquipment?.length > 0 ? (this.props.slot.eligibleEquipment || []) : (this.props.slot.eligibleStaff || [])}
                                        value={this.props.selectedStaffForEquipment}
                                        onChange={this.props.onStaffForEquipmentChange}
                                        getOptionLabel={(option) => option.name || option.description}
                                        getOptionValue={(option) => option.id}
                                        isClearable={false}
                                        isDisabled={!this.props.slot.eligibleStaff?.length && !this.props.slot.eligibleEquipment?.length}
                                        menuPlacement="top"
                                    />
                                </div>
                            )}

                        <FormGroup
                            switch
                            className="mb-3"
                        >
                            <ReactstrapInput
                                type="switch"
                                id="bulkAssignmentSwitch"
                                name="bulkAssignmentSwitch"
                                checked={bulkAssignmentEnabled}
                                onChange={(e) => onBulkAssignmentToggle(e.target.checked)}
                            />
                            <Label
                                check
                                htmlFor="bulkAssignmentSwitch"
                            >
                                <span id="bulkAssignmentSwitch-label">
                                    Bulk Assignment
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        className="ml-1 text-muted"
                                    />
                                </span>
                            </Label>
                        </FormGroup>

                        <UncontrolledTooltip
                            target="bulkAssignmentSwitch-label"
                            hideArrow
                        >
                            Enable this toggle to apply this assignment to every day of this job for the selected week.
                        </UncontrolledTooltip>

                        <SaveButton
                            color="primary"
                            onClick={onSave}
                            disabled={!selectedResource || isSaving}
                            saving={isSaving}
                            className="w-100"
                            label="Save Assignment"
                        />
                    </div>
                </ModalFooter>
            </Modal>
        );
    }
}

export default MobileAssignmentsModal;